import { createApiAction } from '@saucelabs/sl-util-redux';
import { stringify } from 'querystring';
import { actions as notificationActions } from '@saucelabs/sl-container-notifications';
import { utils } from '@saucelabs/sl-module-user';
import { replace } from 'connected-react-router';

import * as actions from './actions';
import * as api from './api';
import * as constants from './types';
import * as util from './../../util';
import * as selectors from './selectors';
import { OWNER_FILTER_KEYS, DEFAULT_GET_OWNERS_OPTIONS, DEFAULT_FILTER } from '../../constants';

import { operations } from './index';

// get filters
export const getFilters = createApiAction(
  constants.GET_FILTERS,
  (payload, meta, dispatch, getState) => api.getFilters(selectors.getFiltersParams(getState())),
);

export const clearAndGetFilters = (payload, meta) => (dispatch) => {
  dispatch(actions.clearSelectedFilters(payload, meta));
  return dispatch(operations.getFilters());
};

export const selectAndGetFilterItems = (payload, meta) => (dispatch) => {
  dispatch(actions.selectFilterItems(payload, meta));
  return dispatch(operations.getFilters());
};

export const getOwners = (
  selectedOwner, options = DEFAULT_GET_OWNERS_OPTIONS,
) => (dispatch, getState) => {
  const userInfo = selectors.getUserInfo(getState());
  const { username, user } = userInfo;
  const ownerSources = utils.getOwnerSources(user);
  const userData = { name: username, id: user.get('user_id') };
  const teamData = { name: user.getUserTeamName(), id: user.getUserTeamId() };
  return Promise.all([
    dispatch(actions.fetchOwnersRequest()),
    ownerSources.addUsername && dispatch(actions.addUsername(userData)),
    ownerSources.addTeam && dispatch(actions.addTeam(teamData)),
    util.isOrganizationScopeEnabled(user) && dispatch(actions.addOrganization()),
    ownerSources.getOrganizationUsers && options.getOrganizationUsers && dispatch(
      actions.getOrganizationUsers(username)),
    ownerSources.getTeams && options.getTeams &&
      dispatch(actions.getTeams()),
  ].filter(Boolean))
    .then(() => dispatch(actions.fetchOwnersSuccess(selectors.getOwners(getState(), selectedOwner)),
    )).catch((error) => {
      dispatch(notificationActions.notifyDanger(
        error.message, 'We\'ve experienced an issue while fetching owners.', true,
      ));
    });
};

export const searchMetadata = (payload) => (dispatch, getState) => {
  const { filterKey, value } = payload;
  const isQueryValid = value && value.length > 2;
  if (!isQueryValid) {
    return Promise.resolve();
  }

  // Owners are not a part of the filters API, handle separately.
  if (filterKey === constants.FILTER_KEY_OWNERS) {
    const { user } = getState();
    if (user.isOrgAdmin()) {
      return dispatch(
        actions.fetchOwnersFiltered({ queryParams: { phrase: value } }),
      );
    }
    return dispatch(notificationActions.notifyDanger(
      'User doesn\'t have sufficient permissions to perform this search.', true,
    ));
  }

  return Promise.all([
    dispatch(actions.searchMetadataRequest(filterKey)),
    api.searchMetadata(selectors.getMetadataParams(getState(), payload)),
  ]).then((result) => dispatch(actions.searchMetadataSuccess(result[1].data),
  )).catch(() => dispatch(actions.searchMetadataFailure(filterKey)));
};

export const setInitialFilters = (
  user, location, defaultFilters = DEFAULT_FILTER, ownerOptions,
) => (dispatch, getState) => {
  const {
    initialFilters,
    shouldUpdateURL,
  } = selectors.getInitialFilters(getState(), { defaultFilters });
  const { [OWNER_FILTER_KEYS.OWNERS]: ownersFilter = [], ...filters } = initialFilters;
  const dateFilters = initialFilters.dateFilter ?
    util.getDateFilterFromQueryParams(initialFilters) : null;

  return Promise.all([
    ...(Object.keys(filters).length ? [dispatch(actions.setFiltersFromQueryParams(filters))] : []),
    ...(dateFilters ? [dispatch(actions.selectDateFilter(dateFilters))] : []),
    dispatch(operations.getOwners(ownersFilter, ownerOptions)),
    ...(shouldUpdateURL ? [dispatch(replace({ search: `?${stringify(initialFilters)}` }))] : []),
  ]);
};

export const fetchAndSetFilters = (params) => (dispatch) => (
  dispatch(operations.clearAndGetFilters()).then(() => (
    dispatch(operations.setInitialFilters(params))
  ))
);
